import type { CSSResult } from 'lit';
import { css } from 'lit';

export const figmaAppVariables: CSSResult = css`
  :host {
    /* PRIMITIVE COLORS */

    /* accent */
    --accent-0: rgba(243, 62, 75, 0);
    --accent-100: rgba(243, 62, 75, 0.1);
    --accent-10: #fee7e9;
    --accent-20: #fccfd2;
    --accent-30: #f99fa5;
    --accent-40: #f66f79;
    --accent-50: #f33e4b;
    --accent-60: #f00f1f;
    --accent-70: #c00c19;
    --accent-80: #900913;
    --accent-90: #60060c;

    /* black */
    --black-0: rgba(0, 0, 0, 0);
    --black-100: #000000;
    --black-10: rgba(0, 0, 0, 0.1);
    --black-20: rgba(0, 0, 0, 0.2);
    --black-30: rgba(0, 0, 0, 0.3);
    --black-40: rgba(0, 0, 0, 0.4);
    --black-50: rgba(0, 0, 0, 0.5);
    --black-60: rgba(0, 0, 0, 0.6);
    --black-70: rgba(0, 0, 0, 0.7);
    --black-80: rgba(0, 0, 0, 0.8);
    --black-90: rgba(0, 0, 0, 0.9);

    /* brand */
    --brand-0: rgba(255, 180, 0, 0);
    --brand-100: rgba(255, 180, 0, 0.1);
    --brand-10: #fff0cc;
    --brand-20: #ffe199;
    --brand-30: #ffd266;
    --brand-40: #ffc333;
    --brand-50: #ffb400;
    --brand-60: #cc9000;
    --brand-70: #996c00;
    --brand-80: #664800;
    --brand-85: #332400;

    /* error */
    --error-0: rgba(243, 62, 75, 0);
    --error-100: rgba(243, 62, 75, 0.1);
    --error-10: #fee7e9;
    --error-20: #fccfd2;
    --error-30: #f99fa5;
    --error-40: #f66f79;
    --error-50: #f33e4b;
    --error-60: #f00f1f;
    --error-70: #c00c19;
    --error-80: #900913;
    --error-90: #60060c;

    /* info */
    --info-0: rgba(51, 68, 255, 0);
    --info-100: rgba(51, 68, 255, 0.1);
    --info-10: #d6daff;
    --info-20: #adb4ff;
    --info-30: #858fff;
    --info-40: #5c69ff;
    --info-50: #3344ff;
    --info-60: #2936cc;
    --info-70: #1f2999;
    --info-80: #141b66;
    --info-90: #0a0e33;

    /* neutral */
    --neutral-0: rgba(12, 59, 46, 0);
    --neutral-100: rgba(12, 59, 46, 0.1);
    --neutral-10: #ecf3f1;
    --neutral-20: #cce1db;
    --neutral-30: #a6d3c7;
    --neutral-40: #79cdb6;
    --neutral-50: #26ba91;
    --neutral-60: #1d9070;
    --neutral-70: #15664f;
    --neutral-80: #0c3b2e;
    --neutral-85: #08261e;
    --neutral-90: #03110d;

    /* success */
    --success-0: rgba(76, 175, 80, 0);
    --success-100: rgba(76, 175, 80, 0.1);
    --success-10: #dbefdc;
    --success-20: #b7dfb9;
    --success-30: #94cf96;
    --success-40: #70bf73;
    --success-50: #4caf50;
    --success-60: #3d8c40;
    --success-70: #2e6930;
    --success-80: #1e4620;
    --success-90: #0f2310;

    /* warning */
    --warning-0: rgba(255, 152, 0, 0);
    --warning-100: rgba(255, 152, 0, 0.1);
    --warning-10: #ffeacc;
    --warning-20: #ffd699;
    --warning-30: #ffc166;
    --warning-40: #ffad33;
    --warning-50: #ff9800;
    --warning-60: #cc7a00;
    --warning-70: #995b00;
    --warning-80: #663d00;
    --warning-90: #331e00;

    /* white */
    --white-0: rgba(255, 255, 255, 0);
    --white-100: #ffffff;
    --white-10: rgba(255, 255, 255, 0.1);
    --white-20: rgba(255, 255, 255, 0.2);
    --white-30: rgba(255, 255, 255, 0.3);
    --white-40: rgba(255, 255, 255, 0.4);
    --white-50: rgba(255, 255, 255, 0.5);
    --white-60: rgba(255, 255, 255, 0.6);
    --white-70: rgba(255, 255, 255, 0.7);
    --white-80: rgba(255, 255, 255, 0.8);
    --white-90: rgba(255, 255, 255, 0.9);

    /* yellow */
    --yellow-0: rgba(251, 205, 0, 0);
    --yellow-100: rgba(251, 205, 0, 0.1);
    --yellow-10: #fffefa;
    --yellow-20: #fff4c7;
    --yellow-30: #ffeb94;
    --yellow-40: #ffe161;
    --yellow-50: #ffd72e;
    --yellow-60: #fbcd00;
    --yellow-70: #c7a100;
    --yellow-80: #947800;
    --yellow-90: #614e00;

    /* PRIMITIVE SIZES */

    /* radius */
    --radius-10: 56px;
    --radius-1: 4px;
    --radius-2: 8px;
    --radius-3: 12px;
    --radius-4: 16px;
    --radius-5: 20px;
    --radius-6: 24px;
    --radius-7: 32px;
    --radius-8: 40px;
    --radius-999: 999px;
    --radius-9: 48px;

    /* space */
    --space-10: 56px;
    --space-11: 64px;
    --space-12: 80px;
    --space-13: 96px;
    --space-14: 128px;
    --space-15: 160px;
    --space-16: 192px;
    --space-17: 224px;
    --space-18: 256px;
    --space-19: 512px;
    --space-1: 4px;
    --space-2: 8px;
    --space-3: 12px;
    --space-4: 16px;
    --space-5: 20px;
    --space-6: 24px;
    --space-7: 32px;
    --space-8: 40px;
    --space-9: 48px;

    /* SEMANTIC COLORS */

    /* background */
    --background-accent: var(--accent-50);
    --background-body: var(--neutral-10);
    --background-brand: var(--brand-50);
    --background-primary-inverse: var(--neutral-80);
    --background-primary-object: var(--white-100);
    --background-primary: var(--white-100);
    --background-quaternary: var(--neutral-30);
    --background-secondary: var(--neutral-10);
    --background-state-accent-subtle: var(--accent-10);
    --background-state-accent: var(--accent-50);
    --background-state-brand-subtle: var(--brand-10);
    --background-state-brand: var(--brand-50);
    --background-state-error-subtle: var(--error-10);
    --background-state-error: var(--error-50);
    --background-state-info-subtle: var(--info-10);
    --background-state-info: var(--info-50);
    --background-state-neutral-subtle: var(--background-tertiary);
    --background-state-neutral: var(--black-60);
    --background-state-success-subtle: var(--success-10);
    --background-state-success: var(--success-50);
    --background-state-warning-subtle: var(--brand-10);
    --background-state-warning: var(--brand-50);
    --background-tertiary: var(--neutral-20);

    /* border */
    --border-accent: var(--accent-50);
    --border-brand: var(--brand-50);
    --border-primary: var(--neutral-20);
    --border-secondary: var(--neutral-90);
    --border-state-error: var(--error-50);
    --border-state-info: var(--info-50);
    --border-state-success: var(--success-50);
    --border-state-warning: var(--warning-50);
    --border-tertiary: var(--neutral-90);

    /* gradient */
    --gradient-body: rgba(255, 255, 255, 0);
    --gradient-bottom: rgba(255, 255, 255, 0);
    --gradient-light-bottom: rgba(255, 255, 255, 0);
    --gradient-light-left: rgba(255, 255, 255, 0);
    --gradient-light-right: rgba(255, 255, 255, 0);
    --gradient-top: rgba(255, 255, 255, 0);

    /* icon */
    --icon-accent-subtle: var(--accent-10);
    --icon-accent: var(--accent-50);
    --icon-brand-subtle: var(--brand-10);
    --icon-brand: var(--brand-50);
    --icon-primary-inverse: var(--white-100);
    --icon-primary: var(--neutral-90);
    --icon-secondary-subtle: var(--neutral-20);
    --icon-secondary: var(--neutral-70);
    --icon-state-error-subtle: var(--error-10);
    --icon-state-error: var(--error-50);
    --icon-state-info-subtle: var(--info-10);
    --icon-state-info: var(--info-50);
    --icon-state-success-subtle: var(--success-10);
    --icon-state-success: var(--success-50);
    --icon-state-warning-subtle: var(--warning-10);
    --icon-state-warning: var(--warning-50);

    /* text */
    --text-accent: var(--accent-50);
    --text-brand: var(--brand-50);
    --text-link-disabled: var(--neutral-70);
    --text-link-hover: var(--accent-60);
    --text-link-secondary-disabled: var(--neutral-70);
    --text-link-secondary-hover: var(--accent-60);
    --text-link-secondary: var(--accent-50);
    --text-link: var(--accent-50);
    --text-on-dark-image: var(--white-100);
    --text-on-light-image: var(--neutral-90);
    --text-primary-inverse: var(--neutral-10);
    --text-primary: var(--neutral-90);
    --text-quaternary: var(--neutral-70);
    --text-secondary: var(--neutral-85);
    --text-state-error: var(--error-50);
    --text-state-info: var(--info-50);
    --text-state-success: var(--success-50);
    --text-state-warning: var(--warning-50);
    --text-tertiary: var(--neutral-80);

    /* SEMANTIC SIZES */

    /* font */
    --font-letter-spacing-body-10: -0.20000000298023224px;
    --font-letter-spacing-body-12: -0.23999999463558197px;
    --font-letter-spacing-body-14: -0.2800000011920929px;
    --font-letter-spacing-body-16: -0.3199999928474426px;
    --font-letter-spacing-body-18: -0.36000001430511475px;
    --font-letter-spacing-body-20: -0.4000000059604645px;
    --font-letter-spacing-body-24: -0.47999998927116394px;
    --font-letter-spacing-body-8: -0.1599999964237213px;
    --font-letter-spacing-heading-10: -0.20000000298023224px;
    --font-letter-spacing-heading-12: -0.23999999463558197px;
    --font-letter-spacing-heading-14: -0.2800000011920929px;
    --font-letter-spacing-heading-16: -0.3199999928474426px;
    --font-letter-spacing-heading-18: -0.36000001430511475px;
    --font-letter-spacing-heading-20: -0.4000000059604645px;
    --font-letter-spacing-heading-24: -0.47999998927116394px;
    --font-letter-spacing-heading-32: -0.6399999856948853px;
    --font-letter-spacing-heading-36: -0.7200000286102295px;
    --font-letter-spacing-heading-8: -0.1599999964237213px;
    --font-letter-spacing-title-10: -0.20000000298023224px;
    --font-letter-spacing-title-12: -0.23999999463558197px;
    --font-letter-spacing-title-14: -0.2800000011920929px;
    --font-letter-spacing-title-16: -0.3199999928474426px;
    --font-letter-spacing-title-18: -0.36000001430511475px;
    --font-letter-spacing-title-20: -0.4000000059604645px;
    --font-letter-spacing-title-24: -0.47999998927116394px;
    --font-letter-spacing-title-8: -0.1599999964237213px;
    --font-line-height-body-10: 15px;
    --font-line-height-body-12: 18px;
    --font-line-height-body-14: 21px;
    --font-line-height-body-16: 24px;
    --font-line-height-body-18: 27px;
    --font-line-height-body-20: 30px;
    --font-line-height-body-24: 36px;
    --font-line-height-body-8: 12px;
    --font-line-height-heading-10: 12px;
    --font-line-height-heading-12: 18px;
    --font-line-height-heading-14: 17px;
    --font-line-height-heading-16: 19px;
    --font-line-height-heading-18: 22px;
    --font-line-height-heading-20: 24px;
    --font-line-height-heading-24: 29px;
    --font-line-height-heading-32: 38px;
    --font-line-height-heading-36: 43px;
    --font-line-height-heading-8: 10px;
    --font-line-height-title-10: 12px;
    --font-line-height-title-12: 18px;
    --font-line-height-title-14: 17px;
    --font-line-height-title-16: 19px;
    --font-line-height-title-18: 22px;
    --font-line-height-title-20: 24px;
    --font-line-height-title-24: 29px;
    --font-line-height-title-8: 10px;
    --font-size-body-10: 10px;
    --font-size-body-12: 12px;
    --font-size-body-14: 14px;
    --font-size-body-16: 16px;
    --font-size-body-18: 18px;
    --font-size-body-20: 20px;
    --font-size-body-24: 24px;
    --font-size-body-8: 8px;
    --font-size-heading-10: 10px;
    --font-size-heading-12: 12px;
    --font-size-heading-14: 14px;
    --font-size-heading-16: 16px;
    --font-size-heading-18: 18px;
    --font-size-heading-20: 20px;
    --font-size-heading-24: 24px;
    --font-size-heading-32: 32px;
    --font-size-heading-36: 36px;
    --font-size-heading-8: 8px;
    --font-size-title-10: 10px;
    --font-size-title-12: 12px;
    --font-size-title-14: 14px;
    --font-size-title-16: 16px;
    --font-size-title-18: 18px;
    --font-size-title-20: 20px;
    --font-size-title-24: 24px;
    --font-size-title-8: 8px;

    /* radius */
    --radius-components-badge: var(--radius-rounded);
    --radius-components-button: var(--radius-rounded);
    --radius-components-input: var(--radius-rounded);
    --radius-components-sidebar-nav: var(--radius-rounded);
    --radius-components-tag-medium: var(--radius-rounded);
    --radius-components-tag-small: var(--radius-rounded);
    --radius-primary: var(--radius-6);
    --radius-quaternary: var(--radius-2);
    --radius-rounded: var(--radius-999);
    --radius-secondary: var(--radius-4);
    --radius-tertiary: var(--radius-3);

    /* COMPONENTS */

    /* components-badges */
    --components-badges-badge-bg-error: var(--background-state-error-subtle);
    --components-badges-badge-bg-info: var(--background-state-info-subtle);
    --components-badges-badge-bg-success: var(--background-state-success-subtle);
    --components-badges-badge-bg-warning: var(--background-state-warning-subtle);
    --components-badges-badge-fg-error: var(--text-state-error);
    --components-badges-badge-fg-info: var(--text-state-info);
    --components-badges-badge-fg-success: var(--text-state-success);
    --components-badges-badge-fg-warning: var(--text-state-warning);
    --components-badges-badge-icon-error-bg: var(--background-state-error-subtle);
    --components-badges-badge-icon-error-fg: var(--icon-state-error);
    --components-badges-badge-icon-info-bg: var(--background-state-info-subtle);
    --components-badges-badge-icon-info-fg: var(--icon-state-info);
    --components-badges-badge-icon-neutral-bg: var(--background-tertiary);
    --components-badges-badge-icon-neutral-fg: var(--icon-secondary);
    --components-badges-badge-icon-primary-bg: var(--background-state-brand-subtle);
    --components-badges-badge-icon-primary-fg: var(--icon-brand);
    --components-badges-badge-icon-success-bg: var(--background-state-success-subtle);
    --components-badges-badge-icon-success-fg: var(--icon-state-success);
    --components-badges-badge-icon-warning-bg: var(--background-state-warning-subtle);
    --components-badges-badge-icon-warning-fg: var(--icon-state-warning);
    --components-badges-badge-tag-bg-accent-invert: var(--background-state-accent-subtle);
    --components-badges-badge-tag-bg-accent: var(--background-accent);
    --components-badges-badge-tag-bg-dark-invert: var(--background-state-neutral-subtle);
    --components-badges-badge-tag-bg-dark: var(--background-state-neutral);
    --components-badges-badge-tag-bg-error-invert: var(--background-state-error-subtle);
    --components-badges-badge-tag-bg-error: var(--background-state-error);
    --components-badges-badge-tag-bg-info-invert: var(--background-state-info-subtle);
    --components-badges-badge-tag-bg-info: var(--background-state-info);
    --components-badges-badge-tag-bg-neutral-invert: var(--background-state-neutral-subtle);
    --components-badges-badge-tag-bg-neutral: var(--background-state-neutral);
    --components-badges-badge-tag-bg-primary-invert: var(--background-state-accent-subtle);
    --components-badges-badge-tag-bg-primary: var(--background-accent);
    --components-badges-badge-tag-bg-success-invert: var(--background-state-success-subtle);
    --components-badges-badge-tag-bg-success: var(--background-state-success);
    --components-badges-badge-tag-bg-warning-invert: var(--background-state-warning-subtle);
    --components-badges-badge-tag-bg-warning: var(--background-state-warning);
    --components-badges-badge-tag-fg-accent-invert: var(--text-accent);
    --components-badges-badge-tag-fg-accent: var(--text-primary-inverse);
    --components-badges-badge-tag-fg-dark-invert: var(--text-primary);
    --components-badges-badge-tag-fg-dark: var(--text-on-dark-image);
    --components-badges-badge-tag-fg-error-invert: var(--text-state-error);
    --components-badges-badge-tag-fg-error: var(--text-primary-inverse);
    --components-badges-badge-tag-fg-info-invert: var(--text-state-info);
    --components-badges-badge-tag-fg-info: var(--text-on-dark-image);
    --components-badges-badge-tag-fg-neutral-invert: var(--text-primary);
    --components-badges-badge-tag-fg-neutral: var(--text-on-dark-image);
    --components-badges-badge-tag-fg-primary-invert: var(--text-accent);
    --components-badges-badge-tag-fg-primary: var(--text-primary-inverse);
    --components-badges-badge-tag-fg-success-invert: var(--text-state-success);
    --components-badges-badge-tag-fg-success: var(--text-primary-inverse);
    --components-badges-badge-tag-fg-warning-invert: var(--text-state-warning);
    --components-badges-badge-tag-fg-warning: var(--text-primary-inverse);

    /* components-bottom */
    --components-bottom-bar-bg-button: var(--background-accent);
    --components-bottom-bar-bg: var(--background-primary-inverse);
    --components-bottom-bar-fg-button: var(--icon-primary-inverse);
    --components-bottom-bar-fg: var(--neutral-10);
    --components-bottom-bar-icon-active: var(--text-brand);
    --components-bottom-bar-text-active: var(--text-brand);

    /* components-button */
    --components-button-primary-bg-hover: var(--brand-60);
    --components-button-primary-bg: var(--background-brand);
    --components-button-primary-fg: var(--text-primary);
    --components-button-quaternary-bg-hover: var(--background-quaternary);
    --components-button-quaternary-bg: var(--background-tertiary);
    --components-button-quaternary-fg-hover: var(--text-primary);
    --components-button-quaternary-fg: var(--text-primary);
    --components-button-secondary-bg-hover: var(--background-accent);
    --components-button-secondary-bg: var(--background-accent);
    --components-button-secondary-border-hover: var(--accent-60);
    --components-button-secondary-border: var(--border-accent);
    --components-button-secondary-fg-hover: var(--text-primary-inverse);
    --components-button-secondary-fg: var(--text-primary-inverse);
    --components-button-tertiary-bg-hover: var(--background-secondary);
    --components-button-tertiary-bg: var(--background-primary);
    --components-button-tertiary-fg: var(--text-primary);

    /* components-dropdown */
    --components-dropdown-bg-hover: var(--background-secondary);
    --components-dropdown-bg: var(--background-primary);
    --components-dropdown-text-active: var(--text-accent);
    --components-dropdown-text-disabled: var(--neutral-20);
    --components-dropdown-text: var(--text-primary);

    /* components-footer */
    --components-footer-bg: var(--background-primary);
    --components-footer-border: var(--border-primary);
    --components-footer-text: var(--text-quaternary);

    /* components-game */
    --components-game-topbar-bg-primary: var(--background-body);
    --components-game-topbar-bg-secondary: var(--background-body);
    --components-game-topbar-button-bg: var(--background-primary);
    --components-game-topbar-button-fg: var(--background-primary-inverse);
    --components-game-topbar-toggle-bg-active: var(--components-toggles-bg-active);
    --components-game-topbar-toggle-bg-disabled: var(--background-tertiary);
    --components-game-topbar-toggle-fg-active: var(--background-primary-inverse);
    --components-game-topbar-toggle-fg-disabled: var(--background-quaternary);
    --components-game-topbar-toggle-icon-active: var(--background-primary);
    --components-game-topbar-toggle-icon-disabled: var(--background-primary);

    /* components-hamburger */
    --components-hamburger-bg-primary: var(--background-primary);
    --components-hamburger-bg-secondary: var(--background-secondary);
    --components-hamburger-text-primary: var(--text-primary);
    --components-hamburger-text-secondary: var(--text-secondary);

    /* components-input */
    --components-input-bg-disabled: var(--background-secondary);
    --components-input-border-focus: var(--border-secondary);
    --components-input-border-hover: var(--neutral-30);
    --components-input-border: var(--border-primary);
    --components-input-fg-disabled: var(--text-tertiary);
    --components-input-fg-error: var(--text-state-error);
    --components-input-fg-primary: var(--text-primary);
    --components-input-fg-secondary: var(--text-secondary);

    /* components-menu */
    --components-menu-item-hamburger-bg-active: var(--background-secondary);
    --components-menu-item-hamburger-bg-hover: var(--background-secondary);
    --components-menu-item-hamburger-icon-active: var(--neutral-60);
    --components-menu-item-hamburger-icon-disabled: var(--text-quaternary);
    --components-menu-item-hamburger-icon-primary: var(--neutral-60);
    --components-menu-item-hamburger-icon-secondary: var(--neutral-60);
    --components-menu-item-hamburger-text-active: var(--text-primary);
    --components-menu-item-hamburger-text-disabled: var(--text-quaternary);
    --components-menu-item-hamburger-text: var(--text-primary);
    --components-menu-item-section-bg-active: var(--background-secondary);
    --components-menu-item-section-bg-hover: var(--background-secondary);
    --components-menu-item-section-icon-active: var(--neutral-60);
    --components-menu-item-section-icon-disabled: var(--text-quaternary);
    --components-menu-item-section-icon-primary: var(--neutral-60);
    --components-menu-item-section-icon-secondary: var(--neutral-60);
    --components-menu-item-section-text-active: var(--text-primary);
    --components-menu-item-section-text-disabled: var(--text-quaternary);
    --components-menu-item-section-text: var(--text-primary);
    --components-menu-item-sidebar-bg-active: var(--neutral-85);
    --components-menu-item-sidebar-bg-hover: var(--neutral-85);
    --components-menu-item-sidebar-icon-active: var(--text-brand);
    --components-menu-item-sidebar-icon-disabled: var(--text-quaternary);
    --components-menu-item-sidebar-icon-primary: var(--neutral-60);
    --components-menu-item-sidebar-icon-secondary: var(--neutral-60);
    --components-menu-item-sidebar-text-active: var(--text-primary-inverse);
    --components-menu-item-sidebar-text-disabled: var(--text-quaternary);
    --components-menu-item-sidebar-text: var(--text-primary-inverse);

    /* components-modals */
    --components-modals-bg-body: var(--background-primary);
    --components-modals-bg-button-inverse: var(--background-primary-inverse);
    --components-modals-bg-button: var(--background-primary);
    --components-modals-bg-footer-secondary: var(--background-secondary);
    --components-modals-bg-footer: var(--background-primary);
    --components-modals-bg-header-button-hover: var(--background-secondary);
    --components-modals-bg-header-button: var(--background-primary);
    --components-modals-bg-header-inverse-button-hover: var(--background-primary-inverse);
    --components-modals-bg-header-inverse-button: var(--background-primary-inverse);
    --components-modals-bg-header-inverse: var(--background-primary-inverse);
    --components-modals-bg-header: var(--background-primary);
    --components-modals-bg-overlay: var(--black-80);
    --components-modals-fg-header-button-hover: var(--background-primary-inverse);
    --components-modals-fg-header-button: var(--background-primary-inverse);
    --components-modals-fg-header-inverse-button-hover: var(--background-primary);
    --components-modals-fg-header-inverse-button: var(--background-primary);
    --components-modals-fg-header-inverse: var(--text-primary-inverse);
    --components-modals-fg-header: var(--text-primary);

    /* components-prize */
    --components-prize-progress-bg-active: var(--background-brand);
    --components-prize-progress-bg-disabled: var(--background-primary);
    --components-prize-progress-bg: var(--background-tertiary);
    --components-prize-progress-fg-active: var(--icon-primary-inverse);
    --components-prize-progress-fg-disabled: var(--icon-secondary-subtle);
    --components-prize-progress-fg: var(--icon-primary);

    /* components-scroll */
    --components-scroll-bg-hover: var(--neutral-30);
    --components-scroll-bg: var(--neutral-20);

    /* components-sidebar */
    --components-sidebar-bg-body: var(--background-primary-inverse);

    /* components-step */
    --components-step-item-bg: var(--background-secondary);
    --components-step-item-counter-bg: var(--background-brand);
    --components-step-item-counter-fg: var(--icon-primary-inverse);
    --components-step-item-fg-secondary: var(--text-secondary);
    --components-step-item-fg: var(--text-primary);

    /* components-tab */
    --components-tab-circle-bg-hover: var(--background-tertiary);
    --components-tab-circle-bg: var(--background-primary);
    --components-tab-circle-border-active: var(--background-brand);
    --components-tab-circle-border: var(--background-body);
    --components-tab-circle-counter-bg: var(--background-brand);
    --components-tab-circle-counter-text: var(--text-primary-inverse);
    --components-tab-circle-fg-active: var(--text-accent);
    --components-tab-circle-fg-disabled: var(--neutral-30);
    --components-tab-circle-fg: var(--text-primary);
    --components-tab-horizontal-bg-active: var(--background-brand);
    --components-tab-horizontal-bg-highlighted: var(--background-brand);
    --components-tab-horizontal-bg-hover: var(--background-tertiary);
    --components-tab-horizontal-bg: var(--background-primary);
    --components-tab-horizontal-counter-bg-active: var(--background-tertiary);
    --components-tab-horizontal-counter-bg: var(--background-brand);
    --components-tab-horizontal-counter-text-active: var(--text-primary);
    --components-tab-horizontal-counter-text: var(--text-primary-inverse);
    --components-tab-horizontal-fg-active: var(--text-primary-inverse);
    --components-tab-horizontal-fg-disabled: var(--neutral-30);
    --components-tab-horizontal-fg-highlighted: var(--text-primary-inverse);
    --components-tab-horizontal-fg: var(--text-primary);
    --components-tab-underline-border-active: var(--border-accent);
    --components-tab-underline-border-disabled: var(--border-primary);
    --components-tab-underline-border-hover: var(--border-secondary);
    --components-tab-underline-border: var(--border-primary);
    --components-tab-underline-counter-bg: var(--background-brand);
    --components-tab-underline-counter-text: var(--text-primary-inverse);
    --components-tab-underline-fg-active: var(--text-accent);
    --components-tab-underline-fg-disabled: var(--neutral-30);
    --components-tab-underline-fg: var(--text-primary);
    --components-tab-vertical-bg-active: var(--brand-100);
    --components-tab-vertical-bg-highlighted: var(--background-brand);
    --components-tab-vertical-bg-hover: var(--white-20);
    --components-tab-vertical-bg: var(--white-10);
    --components-tab-vertical-counter-bg: var(--background-brand);
    --components-tab-vertical-counter-text: var(--text-primary-inverse);
    --components-tab-vertical-fg-active: var(--text-brand);
    --components-tab-vertical-fg-disabled: var(--neutral-30);
    --components-tab-vertical-fg-highlighted: var(--text-primary-inverse);
    --components-tab-vertical-fg: var(--text-primary-inverse);

    /* components-tag */
    --components-tag-bg-active: var(--background-primary-inverse);
    --components-tag-bg-disabled: var(--background-secondary);
    --components-tag-bg-hover: var(--background-quaternary);
    --components-tag-bg: var(--background-tertiary);
    --components-tag-text-active: var(--text-primary-inverse);
    --components-tag-text-disabled: var(--neutral-40);
    --components-tag-text: var(--text-primary);

    /* components-toggles */
    --components-toggles-bg-active: var(--background-primary-inverse);
    --components-toggles-border: var(--border-secondary);
    --components-toggles-disabled: var(--neutral-40);
    --components-toggles-fg: var(--icon-primary-inverse);
    --components-toggles-switch-bg-selected-disabled: var(--neutral-40);
    --components-toggles-switch-bg: var(--background-tertiary);
    --components-toggles-switch-fg-disabled: var(--neutral-10);

    /* components-tooltips */
    --components-tooltips-bg: var(--background-primary-inverse);
    --components-tooltips-text: var(--text-primary-inverse);

    /* components-top */
    --components-top-bar-bg-button-active: var(--background-brand);
    --components-top-bar-bg-button: var(--background-primary);
    --components-top-bar-bg: var(--background-body);
    --components-top-bar-fg-button-active: var(--text-primary-inverse);
    --components-top-bar-fg-button-primary: var(--text-primary);
    --components-top-bar-fg-button-secondary: var(--text-brand);

    /* OTHER */

    /* font */
    --font-family-body: Poppins;
    --font-family-heading: DunkelSans;
    --font-family-title: Poppins;
    --font-weight-bold: Bold;
    --font-weight-extrabold: 'SemiCondensed Rounded';
    --font-weight-regular: Regular;
  }
`;
